import { flexRender, Row } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { InlineLoading } from '@carbon/react';
import classNames from 'classnames';
import { ReactNode, useCallback, useEffect, useRef } from 'react';
import { DataTableColumnMeta, InfiniteScrollConfig } from './types';
import { getAlignment, getPinningStyles, titleFromText } from './utils';
import styles from './table-body.module.scss';
import { CellActionButton } from './cell-action-button';

const ALIGN_TO_JUSTIFY: Record<string, string> = {
  right: 'flex-end',
  center: 'center',
  left: 'flex-start',
};

const ROW_HEIGHT = 48;

// ─────────────────────────────────────
// Props
// ─────────────────────────────────────

interface TableBodyProps<TData> {
  rows: Row<TData>[];
  infiniteScroll?: InfiniteScrollConfig;
  isFetchingNextPage?: boolean;
  selectedRowId?: string | null;
  onRowSelect?: (row: TData) => void;
  renderHeader?: () => ReactNode;
  columnCount?: number;
  tableWidth?: number;
  effectiveWidths?: Record<string, number>;
  /** Render function for expanded row content */
  renderRowSubComponent?: (row: Row<TData>) => ReactNode;
  /**
   * True when any column has `meta.wrap: true`. Derived in the parent
   * from column defs (not rows) so the value is stable across empty /
   * loading / loaded states.
   */
  hasWrappingColumn?: boolean;
  error?: Error | string | null;
  /** Cap the scroll body's height (any CSS length) so it scrolls internally
   *  instead of growing the page. @see DataTableProps.maxBodyHeight */
  maxBodyHeight?: string;
  /** Text shown when there are no rows. @see DataTableProps.emptyMessage */
  emptyMessage?: string;
  /** Set of row IDs to highlight with a light background */
  highlightedRowIds?: Set<string>;
  /** True while a fetch is in flight — suppresses the empty-state text so the
   *  initial load shows the progress bar (rendered above) instead of flashing
   *  "No results to display" before the first response lands. */
  isLoading?: boolean;
}

// ─────────────────────────────────────
// Component
// ─────────────────────────────────────

/**
 * Virtualized table body component for rendering large datasets efficiently.
 * Uses @tanstack/react-virtual for row virtualization and supports infinite scroll.
 * Handles row selection, cell formatting, and cell actions.
 * @param {TableBodyProps} props - Component props
 * @param {Row[]} props.rows - TanStack Table row instances to render
 * @param {InfiniteScrollConfig} [props.infiniteScroll] - Infinite scroll configuration
 * @param {boolean} [props.isFetchingNextPage] - Whether a next page fetch is in progress
 * @param {string | null} [props.selectedRowId] - Currently selected row ID
 * @param {Function} [props.onRowSelect] - Callback when row is clicked
 * @param {Function} [props.renderHeader] - Render prop for table header
 * @param {number} [props.columnCount] - Column count for empty state colspan
 * @param {number} [props.tableWidth] - Total table width for sizing
 * @return {JSX.Element} The table body container element
 */
export function TableBody<TData extends Record<string, unknown>>({
  rows,
  infiniteScroll,
  isFetchingNextPage,
  selectedRowId,
  onRowSelect,
  renderHeader,
  columnCount: columnCountProp,
  tableWidth,
  effectiveWidths,
  renderRowSubComponent,
  hasWrappingColumn = false,
  error,
  maxBodyHeight,
  emptyMessage = 'No results to display',
  highlightedRowIds,
  isLoading,
}: TableBodyProps<TData>) {
  const parentRef = useRef<HTMLDivElement>(null);

  // Track whether any rows can expand (affects virtualizer config)
  const hasExpandableRows = !!renderRowSubComponent;

  // Wrap detection comes from the parent (computed from column defs, not
  // rows) so the value is stable across empty / loading / loaded states.
  // Switches the virtualizer to measureElement-based heights so wrapped
  // rows can grow past ROW_HEIGHT. Tables without any wrap column keep
  // the static estimate — preserves the fast path for everything else.
  const enableDynamicRowHeights = hasWrappingColumn;

  // Both expandable rows and wrap-enabled rows render via the per-row
  // <tbody> + measureElement layout. Static-height tables use a single
  // <tbody> with explicit row heights.
  const useMeasuredHeights = hasExpandableRows || enableDynamicRowHeights;

  // Virtualizer with dynamic sizing for expandable / wrapping rows.
  // `getItemKey` keys the size cache by stable row id (not index) so that
  // sort / filter doesn't poison the cache — when rows reorder, each row
  // brings its measured height with it instead of the cached height for
  // index N being applied to whatever row now lives at that index.
  const getItemKey = useCallback(
    (index: number) => rows[index]?.id ?? index,
    [rows]
  );
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    getItemKey,
    overscan: 10,
  });

  const virtualRows = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  // Infinite scroll detection. isFetchingNextPage is intentionally read
  // from a separate prop (not in deps) so loading state toggling doesn't
  // re-trigger the effect before the parent accumulates new rows.
  useEffect(() => {
    if (!infiniteScroll) return;

    const { hasNextPage, fetchNextPage } = infiniteScroll;
    const lastItem = virtualRows.at(-1);

    if (!lastItem) return;

    if (
      lastItem.index >= rows.length - 5 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [virtualRows, rows.length, infiniteScroll]);

  // Handle row click - fires onRowSelect callback
  const handleRowClick = (row: Row<TData>) => {
    onRowSelect?.(row.original);
  };

  // Calculate padding for virtual scroll
  const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0;
  const paddingBottom =
    virtualRows.length > 0
      ? totalSize - virtualRows[virtualRows.length - 1].end
      : 0;

  // Get column count for spacer cells (use prop when rows are empty)
  const columnCount = columnCountProp ?? rows[0]?.getVisibleCells().length ?? 1;

  // Shared row renderer for both expandable and non-expandable tables
  const renderDataRow = (
    row: Row<TData>,
    dataIndex: number,
    isSelected: boolean,
    isExpanded: boolean
  ) => (
    <tr
      key={row.id}
      className={classNames(styles.tableRow, {
        [styles.tableRowAlt]: dataIndex % 2 === 1,
        [styles.tableRowSelected]: isSelected,
        [styles.tableRowClickable]: onRowSelect,
        [styles.tableRowExpanded]: isExpanded,
        [styles.tableRowHighlighted]: highlightedRowIds?.has(row.id),
      })}
      onClick={() => handleRowClick(row)}
      style={
        // Wrap rows grow to content (with a 48px floor); expandable-only
        // and static rows keep a fixed 48px so the parent row height
        // matches master while expanded sub-content renders in a sibling
        // `<tr>` below.
        hasWrappingColumn
          ? { minHeight: `${ROW_HEIGHT}px` }
          : { height: `${ROW_HEIGHT}px` }
      }
    >
      {row.getVisibleCells().map((cell) => {
        const meta = cell.column.columnDef.meta as
          DataTableColumnMeta | undefined;

        // DataTable injects a formatCellValue-based cell for columns that have
        // meta.type but no user-defined cell. All columns therefore have a
        // meaningful cell function, so we always delegate to flexRender.
        const cellContent = flexRender(
          cell.column.columnDef.cell,
          cell.getContext()
        );

        const alignment = getAlignment(meta);
        const justify = ALIGN_TO_JUSTIFY[alignment] ?? 'flex-start';
        const action = meta?.action;

        const cellWidth =
          effectiveWidths?.[cell.column.id] ?? cell.column.getSize();
        const pinStyles = getPinningStyles(cell.column);

        const isSelectColumn = cell.column.id === '_select';
        const truncates = !meta?.wrap && !meta?.overflowVisible;

        return (
          <td
            key={cell.id}
            data-column-id={cell.column.id}
            className={classNames(styles.tableCell, {
              [styles.tableCellPinned]: cell.column.getIsPinned(),
              [styles.tableCellSelect]: isSelectColumn,
              [styles.tableCellOverflowVisible]: meta?.overflowVisible,
            })}
            style={{
              width: cellWidth,
              position: pinStyles.position as React.CSSProperties['position'],
              left: pinStyles.left,
              right: pinStyles.right,
              zIndex: pinStyles.zIndex,
              boxShadow: pinStyles.boxShadow,
              display: meta?.wrap ? 'flex' : undefined,
              alignItems: meta?.wrap ? 'center' : undefined,
              // In wrap mode the td is a flex container and the content div becomes a
              // content-width flex item, so its own `justifyContent` can't align it —
              // apply the column's horizontal alignment to the td itself (else
              // right-aligned numeric columns render flush-left when wrapped).
              justifyContent: meta?.wrap ? justify : undefined,
            }}
          >
            <div
              data-column-content
              className={classNames(
                styles.tableCellContent,
                meta?.wrap
                  ? styles.tableCellContentWrap
                  : meta?.overflowVisible
                    ? styles.tableCellContentOverflowVisible
                    : styles.tableCellContentTruncate,
                {
                  [styles.tableCellContentSelect]: isSelectColumn,
                }
              )}
              style={{ justifyContent: justify }}
              onMouseEnter={truncates ? titleFromText : undefined}
            >
              {action && (
                <CellActionButton action={action} row={row.original} />
              )}
              {cellContent}
            </div>
          </td>
        );
      })}
    </tr>
  );

  return (
    <div
      ref={parentRef}
      className={styles.tableBodyContainer}
      style={maxBodyHeight ? { maxHeight: maxBodyHeight } : undefined}
    >
      <table className={styles.table} style={{ width: tableWidth }}>
        {renderHeader?.()}
        {error ? (
          <tbody>
            <tr>
              <td colSpan={columnCount} className={styles.emptyFilterCell}>
                <p className={styles.emptyFilterTitle}>Something went wrong</p>
                <p className={styles.emptyFilterSubtitle}>
                  {typeof error === 'string' ? error : error.message}
                </p>
              </td>
            </tr>
          </tbody>
        ) : rows.length === 0 ? (
          <tbody>
            <tr>
              <td colSpan={columnCount} className={styles.emptyFilterCell}>
                {!isLoading && (
                  <p className={styles.emptyFilterTitle}>{emptyMessage}</p>
                )}
              </td>
            </tr>
          </tbody>
        ) : useMeasuredHeights ? (
          <>
            {paddingTop > 0 && (
              <tbody>
                <tr>
                  <td
                    colSpan={columnCount}
                    style={{ height: `${paddingTop}px`, padding: 0, border: 0 }}
                  />
                </tr>
              </tbody>
            )}
            {virtualRows.map((virtualRow) => {
              const row = rows[virtualRow.index];
              const isSelected =
                selectedRowId === row.id || row.getIsSelected();
              // getIsExpanded reads a table-wide flag that setExpanded(true) sets for every
              // row; the expander cell guards on getCanExpand (data-table.tsx), so this must too.
              const isExpanded =
                hasExpandableRows && row.getCanExpand() && row.getIsExpanded();

              return (
                <tbody
                  key={row.id}
                  ref={virtualizer.measureElement}
                  data-index={virtualRow.index}
                  className={styles.rowGroup}
                >
                  {renderDataRow(row, virtualRow.index, isSelected, isExpanded)}
                  {isExpanded && renderRowSubComponent && (
                    <tr className={styles.expandedRow}>
                      <td
                        colSpan={columnCount}
                        className={styles.expandedRowCell}
                      >
                        {renderRowSubComponent(row)}
                      </td>
                    </tr>
                  )}
                </tbody>
              );
            })}
            {paddingBottom > 0 && (
              <tbody>
                <tr>
                  <td
                    colSpan={columnCount}
                    style={{
                      height: `${paddingBottom}px`,
                      padding: 0,
                      border: 0,
                    }}
                  />
                </tr>
              </tbody>
            )}
          </>
        ) : (
          <tbody>
            {paddingTop > 0 && (
              <tr>
                <td
                  colSpan={columnCount}
                  style={{ height: `${paddingTop}px`, padding: 0, border: 0 }}
                />
              </tr>
            )}
            {virtualRows.map((virtualRow) => {
              const row = rows[virtualRow.index];
              const isSelected =
                selectedRowId === row.id || row.getIsSelected();

              return renderDataRow(row, virtualRow.index, isSelected, false);
            })}
            {paddingBottom > 0 && (
              <tr>
                <td
                  colSpan={columnCount}
                  style={{
                    height: `${paddingBottom}px`,
                    padding: 0,
                    border: 0,
                  }}
                />
              </tr>
            )}
          </tbody>
        )}
      </table>

      {/* Infinite scroll loading indicator */}
      {isFetchingNextPage && (
        <div className={styles.infiniteScrollLoader}>
          <InlineLoading description="Loading more..." />
        </div>
      )}
    </div>
  );
}
